import {
  canDetach,
  canRedoSession,
  type Clipboard,
  copyClosure,
  type EditAction,
  canUndoSession,
  type EditorSession,
  type EditorState,
  detachActions,
  execute,
  openAngle,
  PASTE_OFFSET,
  pasteActions,
  promoteActions,
  redoLabel,
  redoSession,
  remapSource,
  polygonSideAt,
  reachableAngles,
  slideToAngle,
  solidEdgeAt,
  sealSession,
  selectionCenter,
  transformActions,
  undoLabel,
  undoSession,
  withSelection,
} from '@/application';
import { asConstraintId, type ConstraintId } from '@/domain/constraints';
import {
  type AxesSource,
  DEFAULT_GRAPH_RANGE,
  DEFAULT_GRAPH_SCALE,
  evaluate,
  type EntitySource,
  type Evaluated,
  type MeasureKind,
  variationTableAnchor,
} from '@/domain/construction';
import { fittedDomain, graphWindow } from '@/domain/expression';
import { type Vec2, vec } from '@/domain/math';
import {
  commonStyle,
  createEntity,
  type Entity,
  createIdGenerator,
  DEFAULT_STYLE,
  type EntityId,
  inDrawOrder,
  type SceneDocument,
  type Style,
} from '@/domain/model';
import { baseExtent, baseFromPage, cornerName, orientedBase } from '@/domain/solid';
import { type Euler, type ProjectionKind, type Vec3 } from '@/domain/solid';
import { hitTest, hitTestAll } from '@/engine';
import { type TransformBox } from '@/rendering';

import { DEFAULT_SNAP, type SnapResult, type SnapSettings } from './Snapping';
import { createDefaultTools } from './tools/createTools';
import { SelectTool } from './tools/SelectTool';
import { type TextEditRequest, TextTool } from './tools/text';
import {
  type Modifiers,
  NO_MODIFIERS,
  type Tool,
  type ToolContext,
  type ToolPrompt,
  type ToolResponse,
} from './tools/Tool';

/**
 * Routes input to the active tool and applies what it asks for.
 *
 * The controller owns the mutable parts of interaction — which tool is active, what the
 * overlay should show, what the cursor is — and keeps the *document* immutable behind
 * `EditorState`. Tools stay pure state machines; nothing but this class knows how a
 * `ToolResponse` becomes a new editor state.
 *
 * Pan and zoom are deliberately absent: they are viewport navigation, not editing, and
 * live in `rendering/CameraController` so they keep working whichever tool is active.
 */

export interface ControllerView {
  readonly editor: EditorState;
  /** Ghost geometry for the overlay layer. */
  readonly preview: readonly Evaluated[];
  readonly snap: SnapResult | null;
  /**
   * The active tool's transform frame, when it offers one.
   *
   * Alongside `preview` and `snap` rather than in the store, for the same reason they are: it
   * changes on every pointer move, and a React render per pointer move is the cost the whole
   * architecture is arranged to avoid.
   */
  readonly transformBox: TransformBox | null;
  readonly cursor: string;
  readonly activeToolId: string;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** Label of the edit undo would reverse, for the menu item. */
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;

  /**
   * The style the panel should display.
   *
   * With a selection, the properties its entities agree on — an absent field means they
   * differ. With nothing selected, the style new entities will be given, so a user can
   * pick a colour *before* drawing rather than only after.
   */
  readonly style: Partial<Style>;
  /**
   * A label waiting for the shell to put a text field over it.
   *
   * The controller cannot open one itself: typing needs a real DOM input so a system input
   * method can compose into it, and the input layer has no DOM.
   */
  readonly textEdit: TextEditRequest | null;
  /**
   * Entities to show a halo on without selecting them.
   *
   * For a panel that names a point and has to answer "which one is that?". Pointing at the name
   * lights the point up on the canvas; it is not a selection, because selecting it would drop
   * the thing whose panel is open and take the question away with it.
   *
   * Not in the store: nothing about it belongs to the document, to undo or to a saved file.
   */
  readonly highlight: readonly EntityId[];
  /** What the active tool is waiting for, or null when it needs nothing. */
  readonly prompt: ToolPrompt | null;
  /** Whether `style` describes a selection or the pending style for new entities. */
  readonly styleAppliesToSelection: boolean;
}

export interface ControllerOptions {
  readonly style?: Style;
  readonly snapSettings?: SnapSettings;
  /** Pick radius in screen pixels. */
  readonly pickRadius?: number;
}

const DEFAULT_PICK_RADIUS = 8;

/** How a graph is created, with the defaults spelled out rather than buried. */
export interface FunctionGraphOptions {
  readonly from?: number;
  readonly to?: number;
  readonly scale?: number;
}

/**
 * The default window and scale, re-exported from the domain where they are defined.
 *
 * They moved there when the written-construction language grew a `graph` statement: the
 * toolbar and the script both create graphs, both create axes to read them against, and the
 * two must use the same number or the curve crosses at 2 while the tick says 3.
 */
export { DEFAULT_GRAPH_RANGE, DEFAULT_GRAPH_SCALE };

/**
 * Space between the bottom of a graph's window and the top of its table, in world units.
 *
 * Enough that the two read as two things. Placed once, here, and never again: the table is
 * dragged wherever the user wants it afterwards, so this only has to be a reasonable start.
 */

export class ToolController {
  private readonly tools = new Map<string, Tool>();
  private readonly selectTool = new SelectTool();
  private readonly textTool: TextTool;
  private active: Tool;

  private session: EditorSession;
  private preview: readonly Evaluated[] = [];
  private snapIndicator: SnapResult | null = null;
  private cursor = 'default';

  private pendingStyle: Style;
  private readonly snapSettings: SnapSettings;
  private readonly pickRadius: number;
  private ids: ReturnType<typeof createIdGenerator>;

  /**
   * The last thing copied, held in memory rather than in the system clipboard.
   *
   * Because what is copied here is a *subgraph*, not a picture: ids, references and all. The
   * system clipboard carries text and images between programs, and neither can express "the
   * midpoint of this segment". Copying between two EduSketch windows is a real want and a
   * separate piece of work — serialising the closure to the document format and reading it
   * back — and this is deliberately not a half-version of it.
   */
  private clipboard: Clipboard | null = null;

  /** See {@link ControllerView.highlight}. */
  private highlighted: readonly EntityId[] = [];

  /**
   * Which point of an angle a number typed into it should move.
   *
   * ## Why this is not the lock
   *
   * The first version said "only this one moves" by **locking** the other two, on the reasoning
   * that one idea doing two jobs beats two that could disagree. The jobs turned out to be
   * different ones. A lock is a lasting, deliberate statement — "protect this from editing" —
   * and `hitTestAll` skips a locked entity, so choosing which arm an angle moved quietly made
   * the other two unclickable everywhere in the application, and left them that way.
   *
   * This is a preference about one operation on one mark. It is not geometry, so it is not in
   * the document; it is not worth an undo step; and it must have no effect on anything but the
   * next number typed into that angle.
   */
  private readonly angleMovers = new Map<EntityId, 'a' | 'vertex' | 'c'>();

  private listeners: ((view: ControllerView) => void)[] = [];

  constructor(initial: EditorSession, options: ControllerOptions = {}) {
    this.session = initial;
    this.pendingStyle = options.style ?? DEFAULT_STYLE;
    this.snapSettings = options.snapSettings ?? DEFAULT_SNAP;
    this.pickRadius = options.pickRadius ?? DEFAULT_PICK_RADIUS;
    // Seeded past the document's existing ids so a new entity can never collide with one
    // already in a loaded file.
    this.ids = createIdGenerator(initial.state.document.nextId);

    this.tools.set(this.selectTool.id, this.selectTool);
    for (const tool of createDefaultTools()) this.tools.set(tool.id, tool);
    this.active = this.selectTool;

    /*
     * The registry's text tool, not a second one.
     *
     * A private `new TextTool()` was the first attempt, and it was always empty: the tool the
     * user's clicks reach is the instance in the map, so the pending edit was set on one
     * object and read from another. Finding it here keeps a single instance, which is the
     * only arrangement in which the request can survive the trip to the shell.
     */
    const registered = [...this.tools.values()].find((tool) => tool instanceof TextTool);
    this.textTool = registered ?? new TextTool();
  }

  get view(): ControllerView {
    return {
      editor: this.session.state,
      preview: this.preview,
      snap: this.snapIndicator,
      transformBox: this.currentBox(),
      cursor: this.cursor,
      activeToolId: this.active.id,
      canUndo: canUndoSession(this.session),
      canRedo: canRedoSession(this.session),
      undoLabel: undoLabel(this.session.history),
      redoLabel: redoLabel(this.session.history),
      textEdit: this.textTool.pendingEdit,
      highlight: this.highlighted,
      prompt: this.active.prompt?.() ?? null,
      style: this.effectiveStyle(),
      styleAppliesToSelection: this.session.state.selection.size > 0,
    };
  }

  /**
   * Changes style.
   *
   * With a selection, restyles it. With nothing selected, sets what the *next* entity
   * will look like — the behaviour of every drawing tool, and the reason the panel is
   * usable before anything exists to select.
   */
  setStyle(patch: Partial<Style>): void {
    const selection = [...this.session.state.selection];

    if (selection.length === 0) {
      this.pendingStyle = { ...this.pendingStyle, ...patch };
      this.emit();
      return;
    }

    this.dispatch({
      actions: [{ kind: 'set-style', ids: selection, style: patch }],
      label: styleLabel(patch),
      sealHistory: true,
    });
  }

  /* ------------------------------------------------------------ panel commands */

  /**
   * Adds a whole construction to the drawing.
   *
   * Used by the written-construction panel. **Added, not substituted**: a user who writes a
   * script to produce a triangle almost always wants it beside what they already have, and
   * a command that silently wiped the canvas would be the worst possible reading of "build".
   *
   * Ids are reassigned from this controller's generator, so a script's `s1` cannot collide
   * with an `s1` already in the document — and every reference inside the script is remapped
   * with them, or the construction would arrive pointing at the wrong objects.
   */
  insertDocument(incoming: SceneDocument): readonly EntityId[] {
    const remap = new Map<EntityId, EntityId>();
    for (const entity of incoming.entities.values()) {
      remap.set(entity.id, this.ids.next('g'));
    }

    const zBase = this.session.state.document.entities.size;
    const actions: EditAction[] = [];
    const created: EntityId[] = [];

    for (const entity of incoming.entities.values()) {
      const id = remap.get(entity.id);
      if (id === undefined) continue;
      created.push(id);
      actions.push({
        kind: 'add-entity',
        entity: {
          ...entity,
          id,
          source: remapSource(entity.source, remap),
          zIndex: zBase + entity.zIndex,
        },
      });
    }

    if (actions.length === 0) return [];

    this.dispatch({
      actions,
      selection: created,
      label: 'Build from text',
      sealHistory: true,
    });
    return created;
  }

  /**
   * Takes a copy of the selection, closed over everything it is built on.
   *
   * Reports whether there was anything to take, so a caller can leave the keystroke to the
   * browser when there is not — Ctrl+C on an empty canvas should still copy whatever the page
   * has selected rather than being swallowed.
   */
  copy(): boolean {
    const entities = copyClosure(this.session.state.document, this.session.state.selection);
    if (entities.length === 0) return false;

    this.clipboard = { entities, pastes: 0 };
    return true;
  }

  /** Copies, then removes what was selected. The closure is copied; only the selection goes. */
  cut(): boolean {
    if (!this.copy()) return false;

    const actions: EditAction[] = [...this.session.state.selection].map((id) => ({
      kind: 'remove-entity' as const,
      id,
    }));
    this.dispatch({ actions, selection: [], label: 'Cut', sealHistory: true });
    return true;
  }

  /**
   * Puts the clipboard back, clear of what is already there, and selects what arrived.
   *
   * Each paste from the same clipboard lands one step further than the last, so pasting three
   * times gives three copies a user can see and take hold of rather than a stack that looks
   * like one. Selecting the result is what makes "paste, then drag it into place" work without
   * having to find the new thing first.
   */
  paste(): boolean {
    const clipboard = this.clipboard;
    if (clipboard === null) return false;

    const step = PASTE_OFFSET * (clipboard.pastes + 1);
    const { actions, created } = pasteActions(
      this.session.state.document,
      clipboard.entities,
      () => this.ids.next('g'),
      vec.vec2(step, -step)
    );
    if (actions.length === 0) return false;

    this.clipboard = { ...clipboard, pastes: clipboard.pastes + 1 };
    this.dispatch({ actions, selection: created, label: 'Paste', sealHistory: true });
    return true;
  }

  /**
   * Copies and pastes in one step, without disturbing the clipboard.
   *
   * The distinction matters in use: a teacher building four variants copies the figure once and
   * then duplicates repeatedly, and a duplicate that overwrote the clipboard would lose the
   * original after the first one.
   */
  duplicate(): boolean {
    const entities = copyClosure(this.session.state.document, this.session.state.selection);
    if (entities.length === 0) return false;

    const { actions, created } = pasteActions(
      this.session.state.document,
      entities,
      () => this.ids.next('g'),
      vec.vec2(PASTE_OFFSET, -PASTE_OFFSET)
    );
    if (actions.length === 0) return false;

    this.dispatch({ actions, selection: created, label: 'Duplicate', sealHistory: true });
    return true;
  }

  /**
   * The topmost entity under a world point, at the current pick tolerance.
   *
   * Here rather than in the shell because the tolerance is the controller's: it is a screen
   * distance divided by the zoom, and a menu that opened on something the same click would not
   * have selected would be a quiet disagreement between two hit tests.
   */
  entityAt(world: Vec2): EntityId | null {
    const hit = hitTest(this.session.state.scene, world, this.pickRadius / this.currentZoom);
    return hit === null ? null : hit.id;
  }

  /**
   * Lifts the solid edge under a point out as a segment of its own.
   *
   * "nhiều khi muốn 1 đoạn nào trong khối có style khác (màu sắc hay kiểu nét)". A solid is one
   * entity with one style, so the edge has to *become* something before it can be styled — and
   * once it is a segment every control already built reaches it. See `promoteActions`.
   *
   * Reports whether it found an edge, so the gesture can fall through to ordinary selection on
   * a solid whose drawn lines are silhouettes rather than edges.
   */
  promoteEdgeAt(world: Vec2): boolean {
    /*
     * A polygon's side first, because its corners are already objects: the side is a segment
     * between two of them, with no new points to make. A base drawn with the polygon tool has
     * sides too, and one of them is the one the question is about.
     */
    const side = this.sideUnder(world);
    if (side !== null) {
      const already = this.segmentJoining(side[0], side[1]);
      if (already !== null) {
        this.select([already]);
        return true;
      }

      const id = this.ids.next('e');
      this.dispatch({
        actions: [
          {
            kind: 'add-entity',
            entity: createEntity(id, { op: 'segment', a: side[0], b: side[1] }, this.pendingStyle),
          },
        ],
        selection: [id],
        label: 'Tách cạnh',
        sealHistory: true,
      });
      return true;
    }

    const found = this.edgeUnder(world);
    if (found === null) return false;
    const { solid: under, edge } = found;

    const { actions, created, existing } = promoteActions(
      this.session.state.document,
      under,
      edge,
      this.pendingStyle,
      () => this.ids.next('e')
    );

    // Alt-clicking an edge that has already been lifted means "that one", not "another one".
    if (existing !== null) {
      this.select([existing]);
      return true;
    }
    if (actions.length === 0) return false;

    const segment = created[created.length - 1];
    this.dispatch({
      actions,
      selection: segment === undefined ? [] : [segment],
      label: 'Tách cạnh',
      sealHistory: true,
    });
    return true;
  }

  /**
   * The solid edge under a point, when there is one.
   *
   * Separate from {@link promoteEdgeAt} so the menu can ask before offering: an item that is
   * present and does nothing teaches the user that the menu lies.
   */
  edgeUnder(world: Vec2): { solid: EntityId; edge: readonly [number, number] } | null {
    /*
     * Every entity under the pointer, not only the topmost.
     *
     * A base drawn over a box puts a polygon on top of the very edges this gesture is for, and
     * asking only what is nearest answers "the polygon" and does nothing. Lifting an edge is
     * about the first thing under the cursor that *has* one, which is what a user means when
     * they alt-click a line they can see.
     */
    for (const id of this.stackAt(world)) {
      const value = this.session.state.scene.values.get(id);
      if (value === undefined || value === null) continue;

      const edge = solidEdgeAt(value, world, this.pickRadius / this.currentZoom);
      if (edge !== null) return { solid: id, edge };
    }
    return null;
  }

  /** The polygon side under a point, as the two corner entities it joins. */
  sideUnder(world: Vec2): readonly [EntityId, EntityId] | null {
    for (const id of this.stackAt(world)) {
      const entity = this.session.state.document.entities.get(id);
      const value = this.session.state.scene.values.get(id);
      if (entity === undefined || value === undefined || value === null) continue;

      const side = polygonSideAt(entity, value, world, this.pickRadius / this.currentZoom);
      if (side !== null) return side;
    }
    return null;
  }

  /** Everything under a point, nearest first. */
  private stackAt(world: Vec2): readonly EntityId[] {
    return hitTestAll(this.session.state.scene, world, this.pickRadius / this.currentZoom).map(
      (hit) => hit.id
    );
  }

  /** A segment already joining these two points, whichever way round it was written. */
  private segmentJoining(a: EntityId, b: EntityId): EntityId | null {
    for (const entity of this.session.state.document.entities.values()) {
      if (entity.source.op !== 'segment') continue;
      if (
        (entity.source.a === a && entity.source.b === b) ||
        (entity.source.a === b && entity.source.b === a)
      ) {
        return entity.id;
      }
    }
    return null;
  }

  /**
   * Cuts the selection loose from what it was built on, without moving it.
   *
   * "làm sao để nó liên kết và cách bỏ liên kết giữa 2 khối" — linking is a gesture, and until
   * now unlinking was nothing at all: the only route was to delete the shape and draw it again
   * with snapping held off. A mechanism with no undo is a trap however good the mechanism.
   */
  detachSelection(): boolean {
    const actions = detachActions(
      this.session.state.document,
      this.session.state.scene.values,
      this.session.state.selection
    );
    if (actions.length === 0) return false;

    this.dispatch({ actions, label: 'Tách rời', sealHistory: true });
    return true;
  }

  /**
   * Lights entities up without selecting them, for a panel answering "which one is that?".
   *
   * Emits only when the set actually changes, because this is driven by a pointer moving across
   * a row of buttons and would otherwise repaint the canvas on every frame of the sweep.
   */
  setHighlight(ids: readonly EntityId[]): void {
    const same =
      ids.length === this.highlighted.length &&
      ids.every((id, index) => this.highlighted[index] === id);
    if (same) return;

    this.highlighted = ids;
    this.emit();
  }

  /** Whether the selection is tied to anything, so a menu can offer the command truthfully. */
  get canDetach(): boolean {
    return canDetach(
      this.session.state.document,
      this.session.state.scene.values,
      this.session.state.selection
    );
  }

  /**
   * Sets what an angle *is*, by asking the solver to make it so.
   *
   * ## Why this is not editing a caption
   *
   * The number beside a mark has never been data — it is the angle, measured, every time the
   * figure is evaluated. A field that overwrote it would let a drawing say 60° while being 47°,
   * and a figure that lies about itself is the one thing this editor exists to prevent.
   *
   * So typing a number does not change the label: it adds a **constraint**, and the solver moves
   * the figure until the angle really is that. The caption then reads 60° because the angle is
   * 60°. Everything downstream — the right-angle square, a measurement, an export — agrees for
   * free, because there is only ever one answer to what the angle is.
   *
   * ## Which point moves
   *
   * Whichever ones can. Only **free points** are variables (ADR-0003: Tier 2 may never write to
   * something Tier 1 derived), and a *locked* point is a constant — so "move this one and not
   * that one" is said by locking the ones that must stay. One idea doing two jobs, rather than a
   * second mechanism that could disagree with the first.
   *
   * `null` releases the angle, and the figure keeps whatever shape it had settled into.
   */
  setAngleValue(ids: readonly EntityId[], degrees: number | null): boolean {
    const actions: EditAction[] = [];

    for (const id of ids) {
      const entity = this.session.state.document.entities.get(id);
      if (entity?.source.op !== 'angle-mark') continue;

      /*
       * An angle with nothing to move cannot be set, and must not pretend otherwise.
       *
       * All three arms derived — the corners of a solid, points on a polygon built on one — is
       * an angle some other figure decides. Tier 2 may never write to what Tier 1 built, so the
       * solver has no variable to turn and the drawing stays exactly as it was. Storing the
       * constraint anyway leaves a note in the document that can never be satisfied, and takes
       * the number in silence, which is how the field came to look broken.
       */
      /*
       * The arms as *points*, whichever way they were given. A side clicked instead of its end
       * is the same angle, and everything below needs the point.
       */
      const arms = this.armsOf(entity.source);
      if (arms === null) continue;
      if (degrees !== null && this.movableArmsOf(arms) === 0) continue;

      /*
       * Place the arms first, by turning them about the vertex.
       *
       * The solver minimises the *angle error* and not the distance a point travels, so left to
       * itself it stops anywhere on the ray that satisfies the angle — measured, an arm of 198
       * came back as 253.5, and every side sharing that point changed with it. Turning the arms
       * is the one motion that changes the angle and nothing else, and it is constructed rather
       * than searched for, so it is exact and instant.
       *
       * The constraint below is still recorded, and arrives already satisfied: it is there to
       * hold the angle when something else is dragged later, not to find it now.
       */
      if (degrees !== null) {
        /*
         * A solid figure slides; a flat one turns.
         *
         * On a solid the arms are corners and divisions of the body, and turning them on the page
         * lifts them out of the faces they belong to — the drawing would keep its angle and stop
         * being a drawing of that solid. What a solid has instead is *parameters*: a point along
         * an edge can be further along it, and it is still on the edge. That is also how the
         * problem states it — "lấy M trên SA sao cho góc AMB bằng 60°".
         */
        const mover = this.angleMoverOf(id);
        const slid = this.slideArmTo(arms, degrees, mover);
        // Out of reach on the edge it may slide along is a refusal, not a reason to fall back on
        // turning: turning is what a solid must not do.
        if (slid === 'unreachable') continue;
        actions.push(...(slid ?? this.turnArmsTo(arms, (degrees * Math.PI) / 180, mover)));
      }

      // Keyed on the mark, so setting a value twice replaces rather than stacks — and so
      // removing the mark can take its constraint with it.
      const constraintId = angleConstraintId(id);
      if (degrees === null) {
        actions.push({ kind: 'remove-constraint', id: constraintId });
        continue;
      }

      actions.push({
        kind: 'add-constraint',
        constraint: {
          id: constraintId,
          kind: 'fixed-angle',
          a: arms.a,
          vertex: arms.vertex,
          c: arms.c,
          // Signed, on the side the figure is already on — see `signedTargetFor`. A bare
          // magnitude asks the solver for the mirrored configuration half the time.
          value: this.signedTargetFor(arms, degrees),
        },
      });
    }

    if (actions.length === 0) return false;
    this.dispatch({
      actions,
      label: degrees === null ? 'Bỏ số đo góc' : 'Đặt số đo góc',
      sealHistory: true,
    });
    return true;
  }

  /**
   * The edit that slides one of an angle's points along its own edge until the angle is right.
   *
   * `null` when the figure has no such point, or when no position of it reaches the angle. It is
   * a refusal rather than a fallback: the caller's other motion is a rotation on the page, which
   * is the very thing a solid figure must not do.
   *
   * Measured **in space**, which is the only angle a solid figure has — the wedge on the page is
   * its shadow, opened or closed by however the body happens to be turned.
   */
  private slideArmTo(
    mark: { a: EntityId; vertex: EntityId; c: EntityId },
    degrees: number,
    mover: EntityId | null
  ): readonly EditAction[] | 'unreachable' | null {
    const span = this.slideSpanOf(mark, mover);
    if (span === null) return null;

    const found = slideToAngle(span.angleAt, degrees, span.range);
    if (found === null) return 'unreachable';

    return [
      {
        kind: 'update-entity',
        entity: { ...span.point, source: { ...span.point.source, t: found } },
      },
    ];
  }

  /**
   * The one point of a mark that slides, with the angle as a function of how far along it is.
   *
   * One description, used by the search and by the report of what it can reach, so the two can
   * never disagree about which point moves or what it would do.
   */
  private slideSpanOf(
    mark: { a: EntityId; vertex: EntityId; c: EntityId },
    mover: EntityId | null
  ): {
    readonly point: Entity & { source: { op: 'ratio-point'; a: EntityId; b: EntityId; t: number } };
    readonly angleAt: (t: number) => number | null;
    readonly range: { from: number; to: number; at: number };
  } | null {
    for (const armId of [mark.c, mark.a, mark.vertex]) {
      if (mover !== null && armId !== mover) continue;
      const entity = this.session.state.document.entities.get(armId);
      if (entity?.source.op !== 'ratio-point' || entity.locked) continue;

      const from = this.spaceOf(entity.source.a);
      const to = this.spaceOf(entity.source.b);
      if (from === undefined || to === undefined) continue;

      // The other two points stay exactly where they are; only the parameter moves.
      const [spaceA, spaceVertex, spaceC] = [mark.a, mark.vertex, mark.c].map((id) =>
        id === armId ? undefined : this.spaceOf(id)
      );
      const source = entity.source;

      return {
        point: entity as never,
        angleAt: (t: number): number | null => {
          const slid = {
            x: from.x + (to.x - from.x) * t,
            y: from.y + (to.y - from.y) * t,
            z: from.z + (to.z - from.z) * t,
          };
          return angleInSpace(spaceA ?? slid, spaceVertex ?? slid, spaceC ?? slid);
        },
        range: { from: 0, to: 1, at: source.t },
      };
    }
    return null;
  }

  /** Where a point is in space, when it knows. */
  private spaceOf(id: EntityId): Vec3 | undefined {
    const value = this.session.state.scene.values.get(id);
    return value?.kind === 'point' ? value.in3d : undefined;
  }

  /**
   * The edits that turn an angle's arms about its vertex until it reads the wanted angle.
   *
   * Empty when there is no such motion — a vertex-only degree of freedom cannot preserve both
   * arms — and the constraint is then left to the solver, which is the right fallback for the
   * one case where nothing can be preserved anyway.
   */
  private turnArmsTo(
    mark: { a: EntityId; vertex: EntityId; c: EntityId },
    radians: number,
    mover: EntityId | null
  ): readonly EditAction[] {
    const at = (armId: EntityId): Vec2 | null => {
      const value = this.session.state.scene.values.get(armId);
      return value?.kind === 'point' ? value.point : null;
    };
    // Exactly the one point chosen to move, and no other. See `angleMovers`.
    const free = (armId: EntityId): boolean => armId === mover;

    const a = at(mark.a);
    const vertex = at(mark.vertex);
    const c = at(mark.c);
    if (a === null || vertex === null || c === null) return [];

    const placed = openAngle({ a, vertex, c }, { a: free(mark.a), c: free(mark.c) }, radians);
    if (placed === null) return [];

    const moveTo = (armId: EntityId, to: Vec2 | undefined): readonly EditAction[] => {
      if (to === undefined) return [];
      const entity = this.session.state.document.entities.get(armId);
      if (entity?.source.op !== 'free-point') return [];
      return [
        {
          kind: 'update-entity',
          entity: { ...entity, source: { ...entity.source, position: to } },
        },
      ];
    };

    return [...moveTo(mark.a, placed.a), ...moveTo(mark.c, placed.c)];
  }

  /**
   * How many of a mark's three points the solver is allowed to move.
   *
   * Zero means the angle is settled by whatever built those points, and no number can change it.
   * The panel greys the field out on this, so the refusal is visible before anything is typed
   * rather than discovered by typing and seeing nothing happen.
   */
  movableArmsOf(mark: { a: EntityId; vertex: EntityId; c: EntityId }): number {
    /*
     * Two ways a point can move, and a solid figure only has the second.
     *
     * A free point may be put anywhere, and a point *along an edge* may be put anywhere on that
     * edge. The second is the one a solid offers — its corners are fixed by the body — and it is
     * the better kind, because the point cannot leave the figure however far it slides.
     */
    return [mark.a, mark.vertex, mark.c].filter((armId) => this.canMovePoint(armId)).length;
  }

  /** Whether this mark's angle can be set at all. See {@link movableArmsOf}. */
  canSetAngle(id: EntityId): boolean {
    const entity = this.session.state.document.entities.get(id);
    if (entity?.source.op !== 'angle-mark') return false;

    const arms = this.armsOf(entity.source);
    return arms !== null && this.movableArmsOf(arms) > 0;
  }

  /** An angle mark's three points, for a panel that has to name and light them. */
  pointsOfAngle(id: EntityId): { a: EntityId; vertex: EntityId; c: EntityId } | null {
    const entity = this.session.state.document.entities.get(id);
    return entity?.source.op === 'angle-mark' ? this.armsOf(entity.source) : null;
  }

  /**
   * The point an arm of an angle actually means.
   *
   * An arm may be given as a *point* or as the **side itself** — the angle tool accepts both on
   * purpose, because the thing a person points at when they say "this angle" is the two sides.
   * Everything downstream needs the point: the rotation moves a point, and the solver's residual
   * takes three points and skips a constraint it cannot resolve.
   *
   * Measured before this existed: marking `ASB` by clicking the two lines stored the constraint
   * on two segments, the solver silently skipped it, the rotation found no point to move, and
   * the app reported success while the angle stayed exactly where it was.
   *
   * For a segment it is the end that is *not* the vertex. `null` when the side does not touch
   * the vertex at all, where there is no single point the arm opens to.
   */
  private armPointOf(armId: EntityId, vertexId: EntityId): EntityId | null {
    const entity = this.session.state.document.entities.get(armId);
    if (entity === undefined) return null;
    if (entity.source.op !== 'segment') return armId;

    if (entity.source.a === vertexId) return entity.source.b;
    if (entity.source.b === vertexId) return entity.source.a;
    return null;
  }

  /** An angle mark's three points, whichever way its arms were given. */
  private armsOf(mark: { a: EntityId; vertex: EntityId; c: EntityId }): {
    a: EntityId;
    vertex: EntityId;
    c: EntityId;
  } | null {
    const a = this.armPointOf(mark.a, mark.vertex);
    const c = this.armPointOf(mark.c, mark.vertex);
    return a === null || c === null ? null : { a, vertex: mark.vertex, c };
  }

  /**
   * The target the constraint should hold, signed to keep the figure on the side it is on.
   *
   * The residual measures `atan2(cross, dot)`, which runs from −π to π, so a bare positive
   * target asks for the *other* configuration whenever the arms happen to be ordered the other
   * way round. Measured on a hand-drawn pyramid: the arm was turned to −126° as asked, the
   * solver then dragged it across to +126°, and the figure came out mirrored through its own
   * vertex at exactly the right angle — far harder to notice than a wrong angle.
   *
   * A magnitude alone cannot say which of the two configurations is meant, so the sign comes
   * from the drawing as it already stands.
   */
  private signedTargetFor(
    mark: { a: EntityId; vertex: EntityId; c: EntityId },
    degrees: number
  ): number {
    const at = (armId: EntityId): Vec2 | null => {
      const value = this.session.state.scene.values.get(armId);
      return value?.kind === 'point' ? value.point : null;
    };

    const a = at(mark.a);
    const vertex = at(mark.vertex);
    const c = at(mark.c);
    const radians = (degrees * Math.PI) / 180;
    if (a === null || vertex === null || c === null) return radians;

    const u = vec.subtract(a, vertex);
    const w = vec.subtract(c, vertex);
    return (u.x * w.y - u.y * w.x < 0 ? -1 : 1) * radians;
  }

  /**
   * Says which point of an angle the next number should move.
   *
   * A preference, not an edit: nothing is written to the document and nothing is locked. See
   * {@link angleMovers} for why choosing this by locking was the wrong mechanism.
   */
  setAngleMover(mark: EntityId, which: 'a' | 'vertex' | 'c'): void {
    this.angleMovers.set(mark, which);
    this.emit();
  }

  /**
   * The point of an angle that the next number will move.
   *
   * The stated preference when it can still move, and otherwise the first arm that can — the
   * second arm before the first, and the vertex last, because a vertex belongs to both arms and
   * moving it changes the figure twice over.
   */
  angleMoverOf(mark: EntityId): EntityId | null {
    const entity = this.session.state.document.entities.get(mark);
    if (entity?.source.op !== 'angle-mark') return null;

    const arms = this.armsOf(entity.source);
    if (arms === null) return null;

    const asked = this.angleMovers.get(mark);
    if (asked !== undefined && this.canMovePoint(arms[asked])) return arms[asked];

    return [arms.c, arms.a, arms.vertex].find((armId) => this.canMovePoint(armId)) ?? null;
  }

  /** Whether the solver, or a placement, is allowed to put this point somewhere else. */
  private canMovePoint(armId: EntityId): boolean {
    const point = this.session.state.document.entities.get(armId);
    if (point === undefined || point.locked) return false;
    return point.source.op === 'free-point' || point.source.op === 'ratio-point';
  }

  /**
   * The angles a sliding point can actually reach, for a refusal that can be acted on.
   *
   * A point on an edge cannot make every angle: it runs from one end of its edge to the other,
   * and the angle runs with it between two bounds. Saying "45° to 114°" turns a number that did
   * nothing into a number the user can correct; `null` when the mark has no sliding point, where
   * there is no such limit to report.
   */
  reachOf(id: EntityId): { readonly low: number; readonly high: number } | null {
    const mark = this.session.state.document.entities.get(id);
    if (mark?.source.op !== 'angle-mark') return null;

    const arms = this.armsOf(mark.source);
    if (arms === null) return null;

    const span = this.slideSpanOf(arms, this.angleMoverOf(id));
    return span === null ? null : reachableAngles(span.angleAt, span.range);
  }

  /** The angle a mark has been fixed at, in degrees, or `null` when it is free to be measured. */
  fixedAngleOf(id: EntityId): number | null {
    const held = this.session.state.document.constraints.get(angleConstraintId(id));
    /*
     * The **magnitude**. The stored value is signed, because the residual measures from −π to π
     * and the sign is what keeps the figure off the mirrored configuration — but that is a fact
     * about the solver, not about the angle. An angle is 90°, never −90°.
     *
     * Handing the sign back reached the number field, which showed `-9` after the first
     * keystroke of `90`; the second keystroke then made `-90`, which the field refuses, and
     * typing stopped working entirely.
     */
    return held?.kind === 'fixed-angle' ? Math.abs((held.value * 180) / Math.PI) : null;
  }

  /** Whether there is anything to paste, for a menu item that should be greyed out. */
  get canPaste(): boolean {
    return this.clipboard !== null;
  }

  /**
   * Removes entities by name — the menu's delete, as against the Delete key's.
   *
   * The key deletes the selection, which is what a key can mean. A menu was opened *over
   * something*, and that something may not be what is selected.
   */
  remove(ids: readonly EntityId[]): void {
    const present = ids.filter((id) => this.session.state.document.entities.has(id));
    if (present.length === 0) return;

    this.dispatch({
      actions: present.map((id) => ({ kind: 'remove-entity' as const, id })),
      selection: [],
      label: 'Delete',
      sealHistory: true,
    });
  }

  /**
   * Writes a label's content.
   *
   * An empty label is removed rather than left behind: a user who opened an editor and
   * thought better of it would otherwise have an invisible, unselectable object on the
   * canvas and no way to know it is there.
   */
  setText(id: EntityId, content: string): void {
    const entity = this.session.state.document.entities.get(id);
    if (entity?.source.op !== 'text') return;

    if (content === '') {
      this.dispatch({
        actions: [{ kind: 'remove-entity', id }],
        selection: [],
        label: 'Remove label',
        sealHistory: true,
      });
      return;
    }

    this.dispatch({
      actions: [
        { kind: 'update-entity', entity: { ...entity, source: { ...entity.source, content } } },
      ],
      label: 'Edit label',
      sealHistory: true,
    });
  }

  /** Clears the pending edit once the shell has opened, or abandoned, an editor. */
  clearTextEdit(): void {
    if (this.textTool.pendingEdit === null) return;
    this.textTool.pendingEdit = null;
    this.emit();
  }

  /** Asks for an editor over an existing label — the double-click path. */
  requestTextEdit(id: EntityId): void {
    const entity = this.session.state.document.entities.get(id);
    if (entity?.source.op !== 'text') return;
    this.textTool.pendingEdit = { id, at: entity.source.offset };
    this.emit();
  }

  /**
   * Sets the selection outright.
   *
   * The layers panel needs this: clicking a row must select that entity exactly as
   * clicking it on the canvas would, including deselecting everything else.
   */
  select(ids: readonly EntityId[]): void {
    this.cancelActiveGesture();
    this.dispatch({ selection: ids });
  }

  /**
   * Restricts what the audience may do.
   *
   * In an exploring mode the drawing can be dragged, selected, transformed and measured but
   * not restructured: only the tools that declare `restructures: false`, no delete, and any
   * structural action a tool still manages to emit is dropped. Three barriers rather than one
   * because the first two are about *offering* the action and the third is what makes the
   * guarantee true — a shortcut or a stale gesture must not get through a door the UI merely
   * stopped drawing.
   *
   * Measurements are the deliberate exception: adding one is structural, and taking
   * measurements is most of what exploring a construction *is*.
   */
  setRestructuring(allowed: boolean): void {
    this.allowsRestructuring = allowed;
    if (!allowed && this.active.restructures !== false) this.setActiveTool(this.selectTool.id);
    this.emit();
  }

  get allowsRestructure(): boolean {
    return this.allowsRestructuring;
  }

  private allowsRestructuring = true;

  /**
   * Solid parameters: size, tessellation, orientation, projection.
   *
   * Sealed immediately, unlike the rotate tool's per-move edits, because a panel change is
   * one deliberate act rather than a gesture with a beginning and an end.
   */
  setSolid(ids: readonly EntityId[], patch: SolidPatch): void {
    if (ids.length === 0) return;
    this.dispatch({
      actions: [{ kind: 'set-solid', ids, patch }],
      label: patch.projection === undefined ? 'Resize solid' : 'Change projection',
      sealHistory: true,
    });
  }

  /**
   * Where a point sits along its segment, as a fraction from the first end.
   *
   * Sealed immediately, like the other panel edits: typing a fraction is one deliberate act,
   * not a gesture with a beginning and an end.
   */
  setRatio(ids: readonly EntityId[], t: number): void {
    if (ids.length === 0 || !Number.isFinite(t)) return;
    this.dispatch({
      actions: [{ kind: 'set-ratio', ids, t }],
      label: 'Set position on segment',
      sealHistory: true,
    });
  }

  /** How far a blackboard plane leans, and how much of it is drawn. */
  setPlaneFree(ids: readonly EntityId[], patch: { tilt?: number; size?: number }): void {
    if (ids.length === 0) return;
    this.dispatch({
      actions: [{ kind: 'set-plane-free', ids, ...patch }],
      label: patch.size === undefined ? 'Set plane angle' : 'Resize plane',
      sealHistory: true,
    });
  }

  /**
   * How far a turned plane leans, or which of the two answers it is.
   *
   * `sealHistory` because this is a committed decision rather than a gesture in progress: a
   * teacher types 45 and the figure is at 45, one undo away from 60. The panel holds the
   * half-typed text, so nothing reaches here until it is a number.
   */
  setPlaneTurn(ids: readonly EntityId[], patch: { degrees?: number; reverse?: boolean }): void {
    if (ids.length === 0) return;
    if (patch.degrees !== undefined && !Number.isFinite(patch.degrees)) return;
    this.dispatch({
      actions: [{ kind: 'set-plane-turn', ids, ...patch }],
      label: patch.reverse === true ? 'Reverse plane' : 'Set plane angle',
      sealHistory: true,
    });
  }

  /** How an angle mark is drawn: arcs, and whether the value is written. */
  setAngleMark(
    ids: readonly EntityId[],
    patch: { ticks?: number; showValue?: boolean; size?: number; valueOffset?: Vec2 }
  ): void {
    if (ids.length === 0) return;
    this.dispatch({
      actions: [{ kind: 'set-angle-mark', ids, ...patch }],
      label: patch.showValue === undefined ? 'Change angle mark' : 'Show angle value',
      sealHistory: true,
    });
  }

  /**
   * Turns or resizes the selection about its own centre.
   *
   * Exact rather than gestural, on purpose. Dragging a handle is good for "roughly there" and
   * bad for "exactly thirty degrees", and a geometry lesson is full of the second kind. The
   * numbers a teacher wants — 30°, 45°, 90°, half size, double — are typed here and applied
   * once, which is also what makes the operation reversible in a single undo.
   *
   * The centre is the selection's own bounding box, so the figure turns in place rather than
   * orbiting the origin.
   */
  transformSelection(patch: { rotation?: number; scale?: number }): void {
    const selection = [...this.view.editor.selection];
    if (selection.length === 0) return;

    const rotation = patch.rotation ?? 0;
    const scale = patch.scale ?? 1;
    if (rotation === 0 && scale === 1) return;

    const center = selectionCenter(this.view.editor.scene.values, selection);
    const actions = transformActions(this.view.editor.document, selection, {
      center,
      rotation,
      scale,
      translate: vec.vec2(0, 0),
    });
    if (actions.length === 0) return;

    this.dispatch({
      actions,
      label: rotation === 0 ? 'Scale selection' : 'Rotate selection',
      sealHistory: true,
    });
  }

  /**
   * Places an already-read picture on the canvas.
   *
   * Takes the decoded href and size rather than a `File`: reading a file is asynchronous and
   * belongs to the layer that owns the browser, while this layer owns *what the document
   * becomes*. Keeping the seam here is what lets image placement be tested without a DOM.
   */
  placeImage(image: { href: string; width: number; height: number }, at: Vec2): EntityId {
    const id = this.ids.next('img');
    this.dispatch({
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            {
              op: 'image',
              // Anchored at its lower-left corner and centred on the point asked for, so an
              // image arrives where the user is looking rather than off to one side.
              position: vec.vec2(at.x - image.width / 2, at.y - image.height / 2),
              width: image.width,
              height: image.height,
              href: image.href,
            },
            { ...DEFAULT_STYLE, ...this.pendingStyle },
            { zIndex: this.session.state.document.entities.size }
          ),
        },
      ],
      selection: [id],
      label: 'Insert image',
      sealHistory: true,
    });
    return id;
  }

  /**
   * Empties the drawing.
   *
   * ## Why there is no confirmation dialogue
   *
   * A modal asking "are you sure?" is the reflex, and it is the wrong instrument. It stops the
   * one user in a hundred who mis-clicked and taxes the ninety-nine who meant it, and after a
   * week nobody reads it — so it protects nobody and annoys everybody.
   *
   * The real protection is that this is **one undo step**. Clearing by accident costs a
   * `Ctrl+Z`, which is the gesture a user already reaches for without thinking. That is worth
   * more than a dialogue, because it works when the mistake is noticed a minute later.
   *
   * Locked entities go too. "Clear the canvas" that leaves things on the canvas is a worse
   * surprise than the deletion — and the lock is there to stop a stray drag, not to survive an
   * explicit instruction to empty the drawing.
   */
  clearDocument(): void {
    const ids = [...this.session.state.document.entities.keys()];
    if (ids.length === 0) return;

    this.dispatch({
      actions: ids.map((id) => ({ kind: 'remove-entity' as const, id })),
      selection: [],
      label: 'Clear canvas',
      sealHistory: true,
    });
  }

  /**
   * Draws `y = f(x)`.
   *
   * Returns the id so the caller can select it, and `null` when the formula does not parse —
   * which the interface reports beside the field rather than as a failed command. A formula
   * with a typo is an ordinary state of a text box, not an error.
   */
  addFunctionGraph(expression: string, options: FunctionGraphOptions = {}): EntityId | null {
    const scale = options.scale ?? DEFAULT_GRAPH_SCALE;

    /*
     * A range nobody asked for is fitted to the curve; a range somebody typed is obeyed.
     *
     * `DEFAULT_GRAPH_RANGE` is not a statement about the problem — it is what this method uses
     * when handed a formula and nothing else. Drawing a steep curve across all of it produces a
     * figure whose axis is numbered to ten and whose curve stops at five, which is the one thing
     * a graph must not do. `fittedDomain` narrows it to the part worth looking at, and can only
     * narrow, so the default remains the outer bound.
     *
     * The moment either end is given, both are taken as stated and nothing is fitted. A problem
     * that says `[−1; 5]` means all of it.
     */
    const stated = options.from !== undefined || options.to !== undefined;
    const asked = {
      from: options.from ?? DEFAULT_GRAPH_RANGE.from,
      to: options.to ?? DEFAULT_GRAPH_RANGE.to,
    };
    const { from, to } = stated ? asked : fittedDomain(expression, asked.from, asked.to);

    const window = graphWindow(expression, from, to);
    /*
     * The y unit is written into both sources rather than left to be derived.
     *
     * The evaluator would work it out from the same window if the field were absent, so the two
     * would agree today. They would stop agreeing the moment a user changed the axes by hand: the
     * axes would hold their new number and the curve would go on deriving the old one, and a
     * curve that does not sit on its own axes is the one failure a graph must not have. Stating
     * it on both is what the existing `scale` does, for the same reason.
     */
    const scaleY = scale * window.yScale;
    const source: EntitySource = { op: 'function-graph', expression, from, to, scale, scaleY };

    // The same resolution a construction tool uses: nothing is created that would evaluate to
    // nothing, so a broken formula never leaves an invisible entity in the document.
    if (evaluate(source, []) === null) return null;

    const actions: EditAction[] = [];

    /*
     * Axes come with the first graph, and only the first.
     *
     * A curve on a blank canvas says nothing: there is no origin to read it against and no
     * indication of what one unit is, and an export of it is a squiggle. Leaving axes as a
     * separate step the user must remember means most exports go out without them — which is
     * exactly the report that prompted this.
     *
     * Only when there are none already: a second pair drawn over the first is worse than
     * none, because two sets of ticks at slightly different weights read as a printing fault.
     */
    if (!this.hasAxes()) {
      actions.push({
        kind: 'add-entity',
        entity: createEntity(
          this.ids.next('axes'),
          {
            op: 'axes',
            scale,
            /*
             * Fitted to the curve, by the same rule the written form uses.
             *
             * Both paths create axes and both used to size them from the x range alone, which
             * frames a cubic at a third of its height. Sharing `graphWindow` is what stops the
             * toolbar and the script producing different figures for the same function.
             */
            scaleY,
            extent: window.extentX,
            extentY: window.extentY,
            center: window.center,
            step: window.step,
            stepY: window.stepY,
            numbered: true,
          },
          // Grey and thin: a frame of reference is read *through*, and axes as heavy as the
          // curve make a graph that is tiring to look at.
          { ...DEFAULT_STYLE, stroke: '#6b7280', strokeWidth: 1 },
          // Behind everything, so a curve crossing an axis is drawn over it.
          { zIndex: -1 }
        ),
      });
    }

    const id = this.ids.next('fn');
    actions.push({
      kind: 'add-entity',
      entity: createEntity(
        id,
        source,
        { ...DEFAULT_STYLE, ...this.pendingStyle },
        {
          zIndex: this.session.state.document.entities.size,
        }
      ),
    });

    this.dispatch({ actions, selection: [id], label: 'Draw graph', sealHistory: true });
    return id;
  }

  /**
   * Adds the variation table of a graph.
   *
   * Returns `null` when the graph has no table — a function with no derivative, or one with
   * infinitely many stationary points. Refusing here rather than adding an entity that
   * evaluates to nothing is the same rule every construction tool follows: nothing is created
   * that would be invisible, because an invisible object in the list is a puzzle.
   *
   * The table is placed **below** the figure rather than beside it: the space to the right of a
   * graph is where a second curve or a legend goes, and the space under it was empty on every
   * worksheet this was tried on. Where exactly is `variationTableAnchor`'s answer, shared with
   * the `variation` statement so the button and the script cannot put it in two places.
   *
   * ## Why the graph stays selected, unlike every other thing this file creates
   *
   * Because the panel that offered the button is the *graph's* panel, and selecting the table
   * empties it of everything it had to say. An image or a measurement is placed where the cursor
   * is and wants moving next, so selecting it is a kindness; a table is placed at a computed
   * anchor and usually needs nothing.
   *
   * The decisive part is not taste. The panel's message for a graph that already has a table —
   * "kéo để đặt lại chỗ" — is written to be read at exactly this moment, and stealing the
   * selection meant it could never be read at all: the click that created the table also hid
   * the sentence explaining what had just happened. Reported as "the button disappears and I
   * cannot get it back", which is what a panel emptied by its own button looks like.
   */
  addVariationTable(graph: EntityId): EntityId | null {
    const entity = this.session.state.document.entities.get(graph);
    if (entity?.source.op !== 'function-graph') return null;

    const source: EntitySource = {
      op: 'variation-table',
      graph,
      at: variationTableAnchor(entity.source, this.axesSource()),
    };
    if (evaluate(source, [this.session.state.scene.values.get(graph) ?? null]) === null) {
      return null;
    }

    const id = this.ids.next('bbt');
    this.dispatch({
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            source,
            { ...DEFAULT_STYLE, strokeWidth: 1 },
            {
              zIndex: this.session.state.document.entities.size,
            }
          ),
        },
      ],
      selection: [graph],
      label: 'Add variation table',
      sealHistory: true,
    });
    return id;
  }

  /** Whether the drawing already has a frame of reference. */
  private hasAxes(): boolean {
    return this.axesSource() !== null;
  }

  /** The drawing's frame of reference, when it has one. `null` is a figure with no axes. */
  private axesSource(): AxesSource | null {
    for (const entity of this.session.state.document.entities.values()) {
      if (entity.source.op === 'axes') return entity.source;
    }
    return null;
  }

  /** The same pair, by id, for a command that has to edit it rather than read it. */
  private axesId(): EntityId | null {
    for (const entity of this.session.state.document.entities.values()) {
      if (entity.source.op === 'axes') return entity.id;
    }
    return null;
  }

  /**
   * Adds a bare pair of axes.
   *
   * Useful with no graph at all — plotting points, reading coordinates off a figure, showing a
   * translation — which is why it is a command of its own rather than something only the
   * grapher can produce. Returns `null` when the drawing already has a pair.
   */
  addAxes(options: { extent?: number; scale?: number; numbered?: boolean } = {}): EntityId | null {
    if (this.hasAxes()) return null;

    const id = this.ids.next('axes');
    this.dispatch({
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            {
              op: 'axes',
              scale: options.scale ?? DEFAULT_GRAPH_SCALE,
              extent: options.extent ?? DEFAULT_GRAPH_RANGE.to,
              step: 1,
              numbered: options.numbered ?? true,
            },
            { ...DEFAULT_STYLE, stroke: '#6b7280', strokeWidth: 1 },
            { zIndex: -1 }
          ),
        },
      ],
      selection: [id],
      label: 'Add axes',
      sealHistory: true,
    });
    return id;
  }

  /** Changes how a pair of axes is drawn. */
  setAxes(
    ids: readonly EntityId[],
    patch: {
      extent?: number;
      extentY?: number;
      center?: Vec2;
      step?: number;
      stepY?: number;
      scaleY?: number;
      numbered?: boolean;
    }
  ): boolean {
    const actions: EditAction[] = [];

    for (const id of ids) {
      const entity = this.session.state.document.entities.get(id);
      if (entity?.source.op !== 'axes') continue;

      const next: EntitySource = { ...entity.source, ...patch };
      if (evaluate(next, []) === null) return false;
      actions.push({ kind: 'update-entity', entity: { ...entity, source: next } });
    }

    if (actions.length === 0) return false;
    this.dispatch({ actions, label: 'Edit axes', sealHistory: true });
    return true;
  }

  /**
   * Turns a polygon the user drew into the base of a solid.
   *
   * ## Why this gesture and not a form
   *
   * Because the alternative is typing. `base 0 0 240 0 240 90 90 90 90 240 0 240` says an
   * L-shaped floor and no teacher will ever write it — while drawing that outline is thirty
   * seconds with the polygon tool and something they already know how to do. The shape a figure
   * needs is *drawn*; only the depth is a decision, and the depth is the one thing a sensible
   * default can supply.
   *
   * ## Why the drawing is the answer rather than a plan
   *
   * The outline is read as the base's **image**, and the solid is built so its base projects back
   * onto exactly those points — see `baseFromPage`. Reading it as a plan seen from above would
   * shear the shape on the way in, so what the teacher drew is not what they would get, and the
   * one thing this gesture promises is that it is.
   *
   * ## What happens to the polygon
   *
   * It is hidden rather than deleted. Its work is done — the solid draws that outline now, and
   * leaving it visible doubles every line of the base — but deleting it would take with it any
   * construction hanging off its corners, and a gesture that quietly destroys a user's work is
   * not one they can try out. Un-hiding it in the objects list undoes the whole idea by hand.
   *
   * Returns `null` when the outline cannot be a base: fewer than three corners, or three of them
   * in a line. A dent is fine, which is the whole point of having built the exact cutter first.
   */
  solidFromPolygon(polygon: EntityId, kind: 'prism' | 'pyramid'): EntityId | null {
    const entity = this.session.state.document.entities.get(polygon);
    if (entity === undefined) return null;

    const value = this.session.state.scene.values.get(polygon);
    if (value?.kind !== 'polygon' || value.vertices.length < 3) return null;

    const raw = baseFromPage(value.vertices);
    if (raw === null) return null;

    const ring = orientedBase(raw, value.vertices.length);
    if (typeof ring === 'string') return null;

    /*
     * A body about as tall as its floor is wide, which is what a textbook draws when the problem
     * does not say. Too short reads as a slab and too tall as a chimney, and either makes the
     * reader think the proportion is part of the question.
     */
    /*
     * Measured from the ring the solid is really built on, so the width and depth it reports are
     * the shape's own rather than two numbers standing in for it. `baseExtent` wants a placement
     * to size a *regular* base; with a ring in hand those numbers are ignored, so any positive
     * pair will do and zero would be refused.
     */
    const extent = baseExtent(
      value.vertices.length,
      { at: vec.vec2(0, 0), width: 1, height: 1, depth: 1 },
      undefined,
      ring
    );

    /*
     * A body about as tall as its floor is wide, which is what a textbook draws when the problem
     * does not say. Too short reads as a slab and too tall as a chimney, and either makes the
     * reader think the proportion is part of the question.
     */
    const height = Math.max(extent.width, extent.depth) * 0.85;

    /*
     * Where to place it, asked of the evaluator rather than derived.
     *
     * The algebra is not hard — the ring is centred on its own bounding box and the base sits
     * half the height below the middle of the body — and writing it out produced a figure exactly
     * twenty units up and twenty across from the outline it was built on. Worse, a second
     * derivation of the same geometry is how two of them come to disagree a year later.
     *
     * So the solid is evaluated once at the origin, its base corners are read off the answer, and
     * the difference of the two centroids is the placement. That cannot drift from what the
     * drawing does, because it *is* what the drawing does.
     *
     * Centroids rather than corresponding corners: `orientedBase` may walk the ring the other way
     * round to keep the winding convention, which moves nothing on the page and would pair the
     * first corner with the last.
     */
    const trial: EntitySource = {
      op: 'solid-figure',
      solid: kind,
      center: polygon,
      width: extent.width,
      height,
      depth: extent.depth,
      corners: value.vertices.length,
      foot: [],
      baseCorners: raw,
    };

    const body = evaluate(trial, [{ kind: 'point', point: vec.vec2(0, 0) }]);
    const landed = value.vertices.flatMap((_, index) => {
      const corner = evaluate({ op: 'solid-vertex', solid: polygon, index }, [body]);
      return corner?.kind === 'point' ? [corner.point] : [];
    });
    if (landed.length !== value.vertices.length) return null;

    const at = vec.subtract(middleOf(value.vertices), middleOf(landed));

    const centre = this.ids.next('c');
    const figure = this.ids.next('s');

    const actions: EditAction[] = [
      {
        kind: 'add-entity',
        entity: createEntity(centre, { op: 'free-point', position: at }, DEFAULT_STYLE, {
          visible: false,
        }),
      },
      {
        kind: 'add-entity',
        entity: createEntity(
          figure,
          {
            op: 'solid-figure',
            solid: kind,
            center: centre,
            width: extent.width,
            height,
            depth: extent.depth,
            corners: value.vertices.length,
            foot: [],
            baseCorners: raw,
          },
          { ...DEFAULT_STYLE, ...this.pendingStyle },
          { zIndex: this.session.state.document.entities.size }
        ),
      },
    ];

    /*
     * The corners, named, because that is the whole difference between this and the shape tool:
     * a solid nobody can point at is a picture, and every problem opens by naming corners.
     *
     * A pyramid's apex comes last, which is where the solid's own numbering puts it — the same
     * convention the `pyramid S . A B C D` statement follows, so a figure built either way is
     * lettered the same and a teacher moving between the two learns nothing new.
     */
    const total = kind === 'pyramid' ? value.vertices.length + 1 : value.vertices.length * 2;
    for (let index = 0; index < total; index += 1) {
      actions.push({
        kind: 'add-entity',
        entity: {
          ...createEntity(
            this.ids.next('sv'),
            { op: 'solid-vertex', solid: figure, index },
            DEFAULT_STYLE
          ),
          label: cornerName(index, value.vertices.length, kind),
        },
      });
    }

    // Hidden, not removed: the solid draws that outline now, and deleting would take any
    // construction hanging off its corners with it.
    actions.push({ kind: 'set-presentation', ids: [polygon], visible: false });

    this.dispatch({
      actions,
      selection: [figure],
      label: kind === 'prism' ? 'Prism from base' : 'Pyramid from base',
      sealHistory: true,
    });
    return figure;
  }

  /**
   * Marks a graph's notable points, or changes which of them are marked.
   *
   * One object per graph rather than one per click: asking twice edits the first rather than
   * stacking a second set of dots on the same coordinates. Unticking everything leaves it in
   * place drawing nothing, so the boxes stay where they are instead of the panel emptying itself
   * under whoever is using it.
   */
  setGraphMarks(
    graph: EntityId,
    patch: { kinds?: readonly string[]; guides?: boolean }
  ): EntityId | null {
    if (this.session.state.document.entities.get(graph)?.source.op !== 'function-graph') {
      return null;
    }

    const existing = [...this.session.state.document.entities.values()].find(
      (entity) => entity.source.op === 'graph-marks' && entity.source.graph === graph
    );

    if (existing?.source.op === 'graph-marks') {
      const next: EntitySource = { ...existing.source, ...patch };
      this.dispatch({
        actions: [{ kind: 'update-entity', entity: { ...existing, source: next } }],
        label: 'Edit graph marks',
        sealHistory: true,
      });
      return existing.id;
    }

    const id = this.ids.next('gm');
    this.dispatch({
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(
            id,
            { op: 'graph-marks', graph, kinds: patch.kinds ?? [], guides: patch.guides ?? false },
            { ...DEFAULT_STYLE, ...this.pendingStyle },
            { zIndex: this.session.state.document.entities.size }
          ),
        },
      ],
      // The graph keeps the selection: the panel that offered the control is the graph's, and
      // selecting the marks would empty it. Same rule as the variation table.
      selection: [graph],
      label: 'Mark graph',
      sealHistory: true,
    });
    return id;
  }

  /**
   * Sizes the axes to a graph, by the rule that drew them in the first place.
   *
   * Offered as a command because the frame and the curve can be edited apart: widening the x
   * range leaves the axes where they were, and a frame that no longer contains its own curve is
   * the defect the fitted window exists to prevent. One button puts it back.
   *
   * It writes the whole frame — reach, height, centre, both steps and the y unit — because those
   * are one answer. Applying half of `graphWindow`'s result would leave the axes in a state the
   * function never produces.
   *
   * The **curve** is rewritten in the same breath, and has to be. The y unit belongs to the pair:
   * axes carrying one number and a curve plotted against another is a figure whose points do not
   * sit where its own scale says they do. Both edits go out as one action list, so fitting is one
   * press of undo rather than two.
   */
  fitAxesTo(graph: EntityId): boolean {
    const entity = this.session.state.document.entities.get(graph);
    const source = entity?.source;
    if (entity === undefined || source?.op !== 'function-graph') return false;

    const axes = this.axesId();
    if (axes === null) return false;

    const pair = this.session.state.document.entities.get(axes);
    if (pair?.source.op !== 'axes') return false;

    const window = graphWindow(source.expression, source.from, source.to);
    const scaleY = source.scale * window.yScale;

    const nextAxes: EntitySource = {
      ...pair.source,
      scaleY,
      extent: window.extentX,
      extentY: window.extentY,
      center: window.center,
      step: window.step,
      stepY: window.stepY,
    };
    const nextGraph: EntitySource = { ...source, scaleY };

    if (evaluate(nextAxes, []) === null || evaluate(nextGraph, []) === null) return false;

    this.dispatch({
      actions: [
        { kind: 'update-entity', entity: { ...pair, source: nextAxes } },
        { kind: 'update-entity', entity: { ...entity, source: nextGraph } },
      ],
      label: 'Fit axes',
      sealHistory: true,
    });
    return true;
  }

  /** Edits a graph in place. Ignores a change that would make it evaluate to nothing. */
  setFunctionGraph(
    ids: readonly EntityId[],
    patch: FunctionGraphOptions & { expression?: string }
  ): boolean {
    const actions: EditAction[] = [];

    for (const id of ids) {
      const entity = this.session.state.document.entities.get(id);
      if (entity?.source.op !== 'function-graph') continue;

      const next: EntitySource = {
        ...entity.source,
        ...(patch.expression === undefined ? {} : { expression: patch.expression }),
        ...(patch.from === undefined ? {} : { from: patch.from }),
        ...(patch.to === undefined ? {} : { to: patch.to }),
        ...(patch.scale === undefined ? {} : { scale: patch.scale }),
      };

      if (evaluate(next, []) === null) return false;
      actions.push({ kind: 'update-entity', entity: { ...entity, source: next } });
    }

    if (actions.length === 0) return false;
    this.dispatch({ actions, label: 'Edit graph', sealHistory: true });
    return true;
  }

  /** Visibility, lock, label — the flags the layers panel owns. */
  setPresentation(
    ids: readonly EntityId[],
    patch: {
      readonly visible?: boolean;
      readonly locked?: boolean;
      readonly label?: string | null;
      readonly isConstruction?: boolean;
    }
  ): void {
    if (ids.length === 0) return;

    /*
     * A name being typed is one undo step, not one per letter.
     *
     * The name field applies every keystroke — a name has no half-typed states the way a number
     * does, and holding a draft is what lost the name when a user finished by clicking the
     * drawing instead of pressing Enter. The cost of applying each letter is an undo history with
     * `A`, `AB`, `ABC` in it, and `mergeKey` is what the session already offers for exactly this:
     * consecutive edits carrying the same key fold into one.
     *
     * Only when the patch is *nothing but* a label. Hiding and locking are single decisions and
     * each deserves its own step, so those still seal.
     */
    const onlyLabel = Object.keys(patch).length === 1 && patch.label !== undefined;
    const first = ids[0];

    this.dispatch({
      actions: [{ kind: 'set-presentation', ids, ...patch }],
      label: presentationLabel(patch),
      ...(onlyLabel && ids.length === 1 && first !== undefined
        ? { mergeKey: `label:${first}` }
        : { sealHistory: true }),
    });
  }

  /**
   * Moves an entity one place through the draw order.
   *
   * Rather than nudging one z value — which does nothing when neighbours share a z, and
   * the demo document has several that do — the whole order is renumbered contiguously
   * after the swap. Only entities whose z actually moved are written, so the undo step
   * stays as small as the change really was.
   */
  moveInDrawOrder(id: EntityId, delta: number): void {
    const order = [...inDrawOrder(this.session.state.document)];
    const from = order.findIndex((entity) => entity.id === id);
    if (from < 0) return;

    const to = from + delta;
    if (to < 0 || to >= order.length) return;

    const moved = order[from];
    const displaced = order[to];
    if (moved === undefined || displaced === undefined) return;
    order[from] = displaced;
    order[to] = moved;

    const actions = order.flatMap((entity, index) =>
      entity.zIndex === index
        ? []
        : [{ kind: 'set-presentation' as const, ids: [entity.id], zIndex: index }]
    );
    if (actions.length === 0) return;

    this.dispatch({
      actions,
      label: delta < 0 ? 'Send backward' : 'Bring forward',
      sealHistory: true,
    });
  }

  /**
   * Attaches a live measurement to the current selection.
   *
   * The measurement is an ordinary DAG node over the selected entities, so it tracks them
   * from the moment it exists — there is no refresh path to get wrong.
   */
  addMeasurement(kind: MeasureKind): EntityId | null {
    const targets = [...this.session.state.selection];
    if (targets.length === 0) return null;

    const source: EntitySource = {
      op: 'measurement',
      measure: kind,
      targets,
      offset: MEASUREMENT_OFFSET,
    };

    /*
     * A measurement that cannot be taken is not created.
     *
     * The same rule every tool and every other command here follows — nothing is created that
     * would evaluate to nothing, because an entity in the object list that draws nothing is a
     * puzzle the user cannot solve or see. This was the one creation path without it: the
     * slope of a vertical segment is undefined, and asking for it left a row in the list with
     * nothing on the canvas to match.
     *
     * The panel already declines to *offer* a measure the selection cannot support, so this
     * changes nothing a user can reach through it. It is here because the guarantee belongs to
     * the layer that creates the entity, not to whichever view happens to be careful today.
     */
    const parents = targets.map((target) => this.session.state.scene.values.get(target) ?? null);
    if (evaluate(source, parents) === null) return null;

    const id = this.ids.next('m');
    const entity = createEntity(
      id,
      source,
      { ...DEFAULT_STYLE, ...this.pendingStyle },
      { zIndex: this.session.state.document.entities.size }
    );

    this.dispatch({
      actions: [{ kind: 'add-entity', entity }],
      selection: [id],
      label: `Measure ${kind}`,
      sealHistory: true,
    });
    return id;
  }

  /** Copies the style of the current selection onto the pending style. */
  adoptSelectionStyle(): void {
    const current = this.effectiveStyle();
    this.pendingStyle = { ...this.pendingStyle, ...current };
    this.emit();
  }

  private effectiveStyle(): Partial<Style> {
    const selection = this.session.state.selection;
    if (selection.size === 0) return { ...this.pendingStyle };

    const styles: Style[] = [];
    for (const id of selection) {
      const entity = this.session.state.document.entities.get(id);
      if (entity !== undefined) styles.push(entity.style);
    }
    return styles.length === 0 ? { ...this.pendingStyle } : commonStyle(styles);
  }

  /**
   * Reverses the last edit.
   *
   * Returns `false` when there is nothing to undo, so the caller knows whether the key
   * was consumed.
   */
  undo(): boolean {
    const result = undoSession(this.session);
    if (result === null) return false;
    this.session = result.session;
    this.cancelActiveGesture();
    this.emit();
    return true;
  }

  redo(): boolean {
    const result = redoSession(this.session);
    if (result === null) return false;
    this.session = result.session;
    this.cancelActiveGesture();
    this.emit();
    return true;
  }

  /**
   * Abandons any half-built construction.
   *
   * Undo can delete an entity a tool is holding a reference to, so the pending gesture
   * has to go with it — otherwise the next click would build something from an id that
   * no longer exists.
   */
  private cancelActiveGesture(): void {
    /*
     * The response is dispatched, not discarded.
     *
     * There are six ways a gesture can be abandoned — selecting from a panel, undo, redo,
     * switching tools, loading a document, and Escape — and only Escape used to act on what
     * `cancel` returned. A tool that undoes its own half-finished work on the way out worked
     * through one of the six and silently left the drawing half-transformed through the other
     * five.
     *
     * Dispatched *before* `reset`, because dispatching can re-enter (a response may hand the
     * tool back to selection), and a tool that had already forgotten its state would have
     * nothing to hand back. `reset` is idempotent for the same reason.
     *
     * Sealing afterwards closes the other half of the same problem: an abandoned gesture used
     * to leave the history open with its merge key still on top, so the *next* gesture sharing
     * that key merged into the one that was thrown away, and one press of undo reversed both.
     */
    this.dispatch(this.active.cancel());
    this.active.reset();
    this.session = sealSession(this.session);
    this.preview = [];
    this.snapIndicator = null;
  }

  get availableTools(): readonly {
    id: string;
    label: string;
    shortcut?: string;
    group?: string;
  }[] {
    return [...this.tools.values()].map((tool) => ({
      id: tool.id,
      label: tool.label,
      ...(tool.shortcut === undefined ? {} : { shortcut: tool.shortcut }),
      ...(tool.group === undefined ? {} : { group: tool.group }),
    }));
  }

  /** Subscribes to view changes. Returns an unsubscribe function. */
  subscribe(listener: (view: ControllerView) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  setActiveTool(id: string): void {
    const tool = this.tools.get(id);
    if (tool === undefined || tool === this.active) return;

    // Exploring means the tools that cannot restructure — which is what the rule was always
    // for, and is now something a tool declares rather than something this line remembers.
    if (!this.allowsRestructuring && tool.restructures !== false) return;

    // Abandoning a half-built construction when switching tools is the honest behaviour:
    // leaving it pending would resume it later from a state the user has forgotten.
    this.cancelActiveGesture();
    this.session = sealSession(this.session);
    this.active = tool;
    this.cursor = 'default';
    this.emit();
  }

  /* --------------------------------------------------------------- input entry */

  pointerDown(world: Vec2, screen: Vec2, button: number, modifiers: Modifiers): void {
    /*
     * Alt lifts a solid's edge out instead of selecting the solid. Taken here rather than inside
     * the select tool because it is not a way of selecting: it is an edit, and it should work
     * the same whichever tool happens to be in hand.
     */
    if (modifiers.alt && button === 0 && this.promoteEdgeAt(world)) return;

    this.dispatch(
      this.active.onPointerDown?.(this.context(), { world, screen, button, modifiers })
    );
  }

  pointerMove(world: Vec2, screen: Vec2, modifiers: Modifiers = NO_MODIFIERS): void {
    this.dispatch(
      this.active.onPointerMove?.(this.context(), { world, screen, button: -1, modifiers })
    );
  }

  pointerUp(world: Vec2, screen: Vec2, button: number, modifiers: Modifiers): void {
    this.dispatch(this.active.onPointerUp?.(this.context(), { world, screen, button, modifiers }));
  }

  /**
   * Handles a key press.
   *
   * Returns `true` when the key was consumed, so the caller knows whether to
   * `preventDefault` — swallowing every key would break browser shortcuts the user
   * expects to keep working.
   */
  keyDown(key: string, modifiers: Modifiers = NO_MODIFIERS): boolean {
    if (modifiers.primary) {
      const lower = key.toLowerCase();
      // Ctrl+Z / Ctrl+Shift+Z, plus Ctrl+Y for the Windows convention.
      if (lower === 'z') return modifiers.shift ? this.redo() : this.undo();
      if (lower === 'y') return this.redo();

      /*
       * Copy, cut, paste, duplicate. Each reports whether it did anything, so a Ctrl+C with
       * nothing selected falls through to the browser rather than being silently eaten.
       */
      if (lower === 'c') return this.copy();
      if (lower === 'x') return this.cut();
      if (lower === 'v') return this.paste();
      if (lower === 'd') return this.duplicate();

      /*
       * Select all — the shortcut every editor has and this one did not, which left "clear
       * the canvas" as a marquee drag the user had to get exactly right.
       *
       * Locked entities stay out. Locking exists so that a construction can be handed to a
       * class in a state they cannot dismantle, and a select-all that swept them in would
       * undo that in one keystroke — with Delete the obvious next one.
       */
      if (lower === 'a') {
        const selectable = [...this.session.state.document.entities.values()]
          .filter((entity) => !entity.locked)
          .map((entity) => entity.id);
        this.select(selectable);
        return true;
      }
    }

    if (key === 'Escape') {
      /*
       * Escape abandons the gesture first, and the tool only second.
       *
       * A tool that has picks in progress returns a response from `cancel`; an idle one
       * returns null, which is the signal that there was nothing to abandon. So the first
       * Escape clears a half-made construction and leaves the tool in hand — which is what
       * a user who mis-clicked the second of three points wants — and a second Escape drops
       * back to selection.
       *
       * The previous behaviour did both at once, so one stray Escape cost the tool as well
       * as the picks and the user had to go back to the palette.
       */
      const response = this.active.cancel();

      if (response !== null) {
        this.dispatch(response);
        // `dispatch` only emits when something changed, and a cancelled pick may change
        // nothing the response mentions — but the prompt has gone back a step.
        this.emit();
        return true;
      }

      if (this.active !== this.selectTool) this.setActiveTool(this.selectTool.id);
      return true;
    }

    const response = this.active.onKeyDown?.(this.context(), key, modifiers);
    if (response !== null && response !== undefined) {
      this.dispatch(response);
      return true;
    }

    /*
     * Tool shortcuts, only when no primary or alt modifier is held, so Ctrl+S is never
     * "segment".
     *
     * Matching is case-SENSITIVE, and Shift is therefore part of the shortcut rather than
     * something to normalise away. Forty-nine tools do not fit in twenty-six letters, so
     * `m` is Midpoint and `M` is Midpoint of segment. Lower-casing first would silently
     * bind six pairs of tools to the same key and hand the press to whichever was
     * registered first.
     */
    if (!modifiers.primary && !modifiers.alt) {
      for (const tool of this.tools.values()) {
        if (tool.shortcut === key) {
          this.setActiveTool(tool.id);
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Replaces the entire document — opening a file, or restoring one from storage.
   *
   * History is discarded rather than kept: the undo stack holds patches against the
   * *previous* document, and applying one to a different document would produce a state
   * that never existed. A "revert to saved" feature belongs in the file layer, not here.
   */
  replaceSession(session: EditorSession): void {
    this.cancelActiveGesture();
    this.session = session;
    this.ids = createIdGenerator(session.state.document.nextId);
    this.cursor = 'default';
    this.emit();
  }

  /** Clears any in-progress gesture — used when the pointer leaves the canvas. */
  cancel(): void {
    this.dispatch(this.active.cancel());
    this.session = sealSession(this.session);
  }

  /* ------------------------------------------------------------------ internals */

  private context(): ToolContext {
    return {
      document: this.session.state.document,
      scene: this.session.state.scene,
      selection: this.session.state.selection,
      zoom: this.currentZoom,
      tolerance: this.pickRadius / this.currentZoom,
      snapSettings: this.snapSettings,
      nextId: (prefix) => this.ids.next(prefix),
      style: this.pendingStyle,
    };
  }

  private currentZoom = 1;

  /** The camera tells the controller its zoom so tolerances stay screen-relative. */
  setZoom(zoom: number): void {
    this.currentZoom = zoom > 0 ? zoom : 1;
  }

  private dispatch(response: ToolResponse | null | undefined): void {
    if (response === null || response === undefined) return;

    if (!this.allowsRestructuring && blocksInExploreMode(response.actions)) {
      // The last barrier. Selection, preview and cursor from the same response are still
      // honoured, so a blocked gesture feels inert rather than broken.
      response = { ...response, actions: undefined };
    }

    let changed = false;

    if (response.actions !== undefined && response.actions.length > 0) {
      const result = execute(this.session, response.actions, {
        ...(response.anchor === undefined ? {} : { anchor: response.anchor }),
        label: response.label ?? this.active.label,
        mergeKey: response.mergeKey,
      });
      this.session = result.session;
      changed = true;
    }

    // Sealing after the edit, so the edit itself still merges into the gesture and only
    // what comes next starts a new undo step.
    if (response.sealHistory === true) {
      this.session = sealSession(this.session);
    }

    if (response.selection !== undefined) {
      const nextState = withSelection(this.session.state, response.selection as Iterable<EntityId>);
      if (nextState !== this.session.state) {
        this.session = { ...this.session, state: nextState };
        changed = true;
      }
    }

    if (response.preview !== undefined) {
      this.preview = response.preview;
      changed = true;
    }

    if (response.snap !== undefined) {
      this.snapIndicator = response.snap;
      changed = true;
    }

    if (response.cursor !== undefined && response.cursor !== this.cursor) {
      this.cursor = response.cursor;
      changed = true;
    }

    if (response.finished === true && this.active !== this.selectTool) {
      this.setActiveTool(this.selectTool.id);
      return;
    }

    /*
     * The frame is asked for rather than told, so nothing above can have marked it changed —
     * and a gesture that moves the frame without producing an edit is ordinary, not an edge
     * case. A turn that rounds to the angle it already had emits no action; without this the
     * frame would stop following the pointer for as long as that lasted.
     */
    if (this.noteBoxChange()) changed = true;

    if (changed) this.emit();
  }

  /**
   * The frame the active tool wants drawn, or `null`.
   *
   * Built on demand rather than cached, so it cannot go stale. It is cheap — a handful of
   * vector additions over a box the tool is already holding.
   */
  private currentBox(): TransformBox | null {
    return this.active.overlayBox?.(this.context()) ?? null;
  }

  /** Whether the frame differs from the one the listeners were last told about. */
  private noteBoxChange(): boolean {
    const box = this.currentBox();
    if (sameBox(box, this.lastBox)) return false;
    this.lastBox = box;
    return true;
  }

  private lastBox: TransformBox | null = null;

  private emit(): void {
    const view = this.view;
    this.lastBox = view.transformBox;
    for (const listener of this.listeners) listener(view);
  }
}

/**
 * Whether two frames would paint identically.
 *
 * Compared by value because the frame is rebuilt on every query — reference equality would
 * report a change on every pointer move and defeat the purpose of asking.
 */
function sameBox(a: TransformBox | null, b: TransformBox | null): boolean {
  if (a === null || b === null) return a === b;

  const samePoint = (p: Vec2, q: Vec2): boolean => p.x === q.x && p.y === q.y;
  const sameList = (xs: readonly Vec2[], ys: readonly Vec2[]): boolean => {
    if (xs.length !== ys.length) return false;
    for (const [index, x] of xs.entries()) {
      const y = ys[index];
      if (y === undefined || !samePoint(x, y)) return false;
    }
    return true;
  };

  if (!sameList(a.corners, b.corners) || !sameList(a.grips, b.grips)) return false;

  if (a.rotate === null || b.rotate === null) {
    if (a.rotate !== b.rotate) return false;
  } else if (!samePoint(a.rotate.at, b.rotate.at) || !samePoint(a.rotate.from, b.rotate.from)) {
    return false;
  }

  if (a.readout === null || b.readout === null) return a.readout === b.readout;
  return a.readout.text === b.readout.text && samePoint(a.readout.at, b.readout.at);
}

/**
 * Names a style change for the undo menu.
 *
 * Specific where it can be — "Change colour" is far more useful in a history list than
 * "Edit" — and generic when several properties change at once.
 */
/** The subset of a solid's construction parameters a panel may change. */
export interface SolidPatch {
  readonly width?: number;
  readonly height?: number;
  readonly depth?: number;
  readonly sides?: number;
  readonly taper?: number;
  readonly rotation?: Euler;
  readonly projection?: ProjectionKind;
}

/**
 * Whether a response would restructure the drawing.
 *
 * Adding a measurement is exempt: it is structural in the model and is exactly what
 * exploring a construction consists of.
 */
function blocksInExploreMode(actions: readonly EditAction[] | undefined): boolean {
  if (actions === undefined) return false;
  return actions.some((action) => {
    if (action.kind === 'add-entity') return action.entity.source.op !== 'measurement';
    return (
      action.kind === 'remove-entity' ||
      action.kind === 'remove-constraint' ||
      action.kind === 'add-constraint'
    );
  });
}

/** The angle at `vertex`, in degrees, measured in space. */
function angleInSpace(a: Vec3, vertex: Vec3, c: Vec3): number | null {
  const u = { x: a.x - vertex.x, y: a.y - vertex.y, z: a.z - vertex.z };
  const w = { x: c.x - vertex.x, y: c.y - vertex.y, z: c.z - vertex.z };
  const lengths = Math.hypot(u.x, u.y, u.z) * Math.hypot(w.x, w.y, w.z);
  if (lengths === 0) return null;

  const cosine = (u.x * w.x + u.y * w.y + u.z * w.z) / lengths;
  return (Math.acos(Math.min(1, Math.max(-1, cosine))) * 180) / Math.PI;
}

/**
 * The constraint that fixes an angle mark's own angle.
 *
 * Derived from the mark rather than minted, so setting a value twice replaces the first and the
 * document never accumulates two constraints saying different things about one angle.
 */
function angleConstraintId(mark: EntityId): ConstraintId {
  return asConstraintId(`angle:${mark}`);
}

const MEASUREMENT_OFFSET = { x: 0, y: 16 } as const;

/** Names a presentation change for the undo menu. */
function presentationLabel(patch: {
  readonly visible?: boolean;
  readonly locked?: boolean;
  readonly label?: string | null;
  readonly isConstruction?: boolean;
}): string {
  if (patch.visible !== undefined) return patch.visible ? 'Show' : 'Hide';
  if (patch.locked !== undefined) return patch.locked ? 'Lock' : 'Unlock';
  if (patch.isConstruction !== undefined) {
    return patch.isConstruction ? 'Mark as construction' : 'Mark as final';
  }
  if (patch.label !== undefined) return patch.label === null ? 'Remove label' : 'Rename';
  return 'Edit';
}

function styleLabel(patch: Partial<Style>): string {
  const keys = Object.keys(patch);
  // Read into a local so the type narrows: indexing an array yields `string | undefined`
  // under `noUncheckedIndexedAccess`, and a switch over that is not exhaustive.
  const only = keys.length === 1 ? keys[0] : undefined;
  if (only === undefined) return 'Change style';

  switch (only) {
    case 'stroke':
      return 'Change colour';
    case 'fill':
    case 'fillStyle':
      return 'Change fill';
    case 'strokeWidth':
      return 'Change thickness';
    case 'lineStyle':
      return 'Change line style';
    case 'pointStyle':
    case 'pointSize':
      return 'Change point style';
    case 'opacity':
      return 'Change opacity';
    default:
      return 'Change style';
  }
}

/** The average of some points, which is what compares two rings whose order may differ. */
function middleOf(points: readonly Vec2[]): Vec2 {
  return vec.vec2(
    points.reduce((sum, point) => sum + point.x, 0) / points.length,
    points.reduce((sum, point) => sum + point.y, 0) / points.length
  );
}
